import { ControlledDateField, ControlledSelectField } from 'components/Form';
import { ControlledTextField } from 'components/Form/ControlledTextField';
import { GroupHeader } from 'components/GroupHeader';
import { ModalDialog } from 'components/ModalDialog';
import type { WorkOrderStatusEnum } from 'gql/graphql';
import { cnMixSpace, Grid, GridItem, IconCalendar } from 'uikit';
import { formatDateToLocale, formatNumberToRu } from 'utils';
import { requiredBaseRule } from 'utils/constants';

import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { getWorkOrderStatusLabel } from '../../WorkOrder/workOrderConstants';
import type { IInflationData } from '../../WorkOrder/WorkOrderRequisites/WorkOrderRequisitesUpdate';

interface IWorkOrderVersionRequisitesUpdateProps {
    defaultValues: any;
    onCancel: () => void;
}

export const WorkOrderVersionRequisitesUpdate: React.FC<IWorkOrderVersionRequisitesUpdateProps> = (props) => {
    const { defaultValues = {}, onCancel } = props;
    const formRef = useRef<HTMLFormElement>(null);

    const transformDefaultValue = useMemo(
        () => ({
            ...defaultValues,
            rangeDate: [defaultValues?.startDate, defaultValues?.finishDate],
            amountWithTax: formatNumberToRu(defaultValues?.amountWithTax, 2),
            amountWithoutTax: formatNumberToRu(defaultValues?.amountWithoutTax, 2),
            tax: formatNumberToRu(defaultValues?.tax, 2),
            remainder: formatNumberToRu(defaultValues?.remainder, 2),
            inflationRate: defaultValues?.inflationData?.map((item: { inflationRate: IInflationData }) =>
                formatNumberToRu(item.inflationRate, 3),
            ),
            workOrderChangeInternalNumber: defaultValues?.workOrderChange?.internalNumber,
            workOrderChangeSigningDate: formatDateToLocale(defaultValues?.workOrderChange?.signingDate),
        }),
        [defaultValues],
    );

    const { control } = useForm({
        defaultValues: transformDefaultValue,
    });

    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    const workOrderStatusItems = useMemo(() => {
        const statusFollowers = (defaultValues?.statusFollowers || []).map((v: WorkOrderStatusEnum) => ({
            id: v,
            label: getWorkOrderStatusLabel(v),
        }));

        return [...statusFollowers, ...(defaultValues?.status ? [defaultValues?.status] : [])];
    }, [defaultValues?.status, defaultValues?.statusFollowers]);

    const hasChanges = useMemo(
        () => !!defaultValues?.workOrderChange?.internalNumber,
        [defaultValues?.workOrderChange],
    );

    return (
        <ModalDialog title="Данные Наряд-Заказа" isOpen onClose={handleCancel} withTitleCloseButton btnConfirmDisabled>
            <form ref={formRef}>
                <Grid cols={1} style={{ width: 600, padding: 20 }}>
                    <GridItem>
                        <GroupHeader mB="s">Основная информация</GroupHeader>
                        <ControlledSelectField
                            control={control}
                            label="Статус"
                            name="status"
                            items={workOrderStatusItems}
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                        <ControlledTextField
                            control={control}
                            label="Предмет"
                            name="subject"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                        <ControlledTextField
                            control={control}
                            label="Внутренний номер"
                            name="internalNumber"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                        <ControlledTextField
                            control={control}
                            label="Подрядчик"
                            name="contractor"
                            disabled
                            className={cnMixSpace({ mB: 's' })}
                        />
                        <ControlledDateField
                            control={control}
                            label="Дата выпуска"
                            name="signingDate"
                            type="date"
                            className={cnMixSpace({ mB: 'l' })}
                            required
                            rules={requiredBaseRule}
                            disabled
                        />
                        {hasChanges && (
                            <>
                                <GroupHeader mB="s">Данные Изменения к НЗ</GroupHeader>
                                <ControlledTextField
                                    control={control}
                                    label="Номер Изм. к НЗ"
                                    name="workOrderChangeInternalNumber"
                                    disabled
                                    className={cnMixSpace({ mB: 's' })}
                                />
                                <ControlledTextField
                                    control={control}
                                    label="Дата подписания"
                                    name="workOrderChangeSigningDate"
                                    disabled
                                    className={cnMixSpace({ mB: 's' })}
                                />
                            </>
                        )}
                        <GroupHeader mB="s">Сумма наряд-заказа</GroupHeader>
                        <ControlledTextField
                            control={control}
                            label="Сумма с НДС (руб.)"
                            name="amountWithTax"
                            disabled
                            className={cnMixSpace({ mB: 's' })}
                        />
                        <ControlledTextField
                            control={control}
                            label="Сумма без НДС (руб.)"
                            name="amountWithoutTax"
                            disabled
                            className={cnMixSpace({ mB: 's' })}
                        />
                        <ControlledTextField
                            control={control}
                            label="НДС, 20% (руб.)"
                            name="tax"
                            disabled
                            className={cnMixSpace({ mB: 's' })}
                        />
                        <ControlledTextField
                            control={control}
                            label="Остаток без НДС (руб.)"
                            name="remainder"
                            disabled
                            className={cnMixSpace({ mB: 'l' })}
                        />
                        <GroupHeader mB="s">Производство работ</GroupHeader>
                        <ControlledDateField
                            control={control}
                            label="Дата начала и окончания"
                            name="rangeDate"
                            className={cnMixSpace({ mB: 'l' })}
                            type="date-range"
                            required
                            rightSide={[IconCalendar, IconCalendar]}
                            disabled
                        />
                        <GroupHeader mB="s">Коэффициент инфляции</GroupHeader>
                        {defaultValues?.inflationData?.map((item: IInflationData, index: number) => (
                            <div key={item.id}>
                                <ControlledTextField
                                    control={control}
                                    label={`Сезон ${item.year}`}
                                    name={`inflationRate[${index}]`}
                                    disabled
                                    className={cnMixSpace({ mB: 's' })}
                                />
                            </div>
                        ))}
                    </GridItem>
                </Grid>
            </form>
        </ModalDialog>
    );
};

WorkOrderVersionRequisitesUpdate.displayName = 'WorkOrderVersionRequisitesUpdate';
